import { motion } from "framer-motion";
import { Music4, Scissors, Layers3, Sparkles } from "lucide-react";
import SceneShell from "../components/SceneShell";

/**
 * Scene 21 — Automated Editing · "the biggest shift".
 * Three feature pillars + an animated audio-waveform visual that lands the
 * "music-driven cuts" idea, ending on the closing line:
 * "Editing is no longer a manual task. It's a system."
 */

const features = [
  {
    icon: Music4,
    title: "AI beat mapping",
    note: "Detects every kick, snare, and key change automatically.",
  },
  {
    icon: Scissors,
    title: "Music-driven cuts",
    note: "Edits land on the beat — every time, no manual trimming.",
  },
  {
    icon: Layers3,
    title: "Automated assembly",
    note: "Multi-format output rendered in parallel from one source.",
  },
];

// Static-shape waveform with marker positions for "beat cuts"
function Waveform() {
  const bars = Array.from({ length: 60 }, (_, i) =>
    Math.abs(Math.sin(i * 0.42) + Math.cos(i * 0.18) * 0.7) * 0.85 + 0.15,
  );
  const beats = [8, 18, 27, 36, 45, 54];

  return (
    <div className="relative w-full" style={{ height: 96 }}>
      <svg
        viewBox="0 0 600 96"
        preserveAspectRatio="none"
        className="absolute inset-0 w-full h-full"
        aria-hidden="true"
      >
        {bars.map((h, i) => {
          const x = i * 10 + 1;
          const w = 6;
          const barH = h * 80;
          const y = 48 - barH / 2;
          return (
            <motion.rect
              key={i}
              x={x}
              y={y}
              width={w}
              height={barH}
              rx={2}
              initial={{ opacity: 0, scaleY: 0.2 }}
              animate={{ opacity: 1, scaleY: 1 }}
              transition={{
                delay: 0.8 + i * 0.012,
                duration: 0.5,
                ease: "easeOut",
              }}
              fill="rgba(0, 222, 182, 0.55)"
              style={{ transformOrigin: `${x + w / 2}px 48px` }}
            />
          );
        })}
        {/* Beat-cut markers */}
        {beats.map((b, i) => (
          <motion.line
            key={`beat-${b}`}
            x1={b * 10 + 4}
            y1={4}
            x2={b * 10 + 4}
            y2={92}
            stroke="#96C11F"
            strokeWidth={2}
            strokeDasharray="2 4"
            initial={{ opacity: 0, pathLength: 0 }}
            animate={{ opacity: 1, pathLength: 1 }}
            transition={{ delay: 1.6 + i * 0.18, duration: 0.5 }}
          />
        ))}
      </svg>
    </div>
  );
}

export default function Scene21_AutomatedEditing() {
  return (
    <SceneShell variant="ink" align="left" data-testid="scene-automated-editing">
      <motion.div
        className="absolute inset-0 pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2 }}
        style={{
          background:
            "radial-gradient(ellipse at 70% 50%, rgba(0,222,182,0.14) 0%, transparent 60%)",
        }}
      />

      <div className="relative max-w-[1500px] mx-auto w-full">
        <motion.h2
          initial={{ opacity: 0, y: 24, filter: "blur(8px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ duration: 1.0, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="kn-display text-4xl sm:text-5xl lg:text-6xl text-white"
          data-testid="auto-edit-title"
        >
          Importance of{" "}
          <span
            style={{
              background:
                "linear-gradient(135deg, #00deb6 0%, #96C11F 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
            }}
          >
            music.
          </span>
        </motion.h2>

        {/* Waveform with beat markers */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.0, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="mt-12 rounded-2xl glass p-6"
          data-testid="auto-edit-waveform"
        >
          <div className="flex items-center justify-between mb-4">
            <span className="kn-eyebrow text-white/55">
              Audio · with auto-detected cuts
            </span>
            <span className="text-[11px] tracking-[0.18em] uppercase text-white/45 tnum">
              4 beats / bar · 124 bpm
            </span>
          </div>
          <Waveform />
        </motion.div>

        {/* Three features */}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.9,
                  delay: 1.2 + i * 0.18,
                  ease: [0.16, 1, 0.3, 1],
                }}
                className="rounded-2xl p-6 glass"
                data-testid={`auto-edit-feature-${i}`}
              >
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
                  style={{
                    background: "rgba(0,222,182,0.16)",
                    color: "#00deb6",
                    border: "1px solid rgba(0,222,182,0.4)",
                  }}
                >
                  <Icon className="w-5 h-5" />
                </div>
                <div className="text-lg font-bold text-white">{f.title}</div>
                <div
                  className="mt-2 text-sm"
                  style={{ color: "rgba(255,255,255,0.6)" }}
                >
                  {f.note}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </SceneShell>
  );
}
